import type { RouteLocationRaw } from 'vue-router'
import type { TodoCalendar } from '@/api'
import { calendarsBrowseLocation } from '@/todos/entry'
import { mapUrl, parseGeo } from './geo'
import { parseWebTodo } from './webTodo'

// What `/open?uri=<link>` does with a link the browser handed over. `notice` is the key below `openLink.` of a toast to show.
export type OpenLinkResult =
  | { kind: 'route'; location: RouteLocationRaw; notice?: string }
  | { kind: 'external'; url: string; notice?: string }
  | { kind: 'unknown'; notice: string }

export interface OpenLinkDeps {
  calendars: () => Promise<TodoCalendar[]>
  mapProvider: () => Promise<string>
}

export async function resolveOpenLink(uri: string, deps: OpenLinkDeps): Promise<OpenLinkResult> {
  const link = uri.trim()
  if (/^geo:/i.test(link)) {
    const point = parseGeo(link)
    if (!point) return { kind: 'unknown', notice: 'unknownGeo' }
    const provider = await deps.mapProvider()
    // The chosen map is the generic `geo:` link, which would come straight back here.
    return provider === 'geo'
      ? { kind: 'external', url: mapUrl(point, 'osm'), notice: 'geoFallback' }
      : { kind: 'external', url: mapUrl(point, provider) }
  }

  const target = parseWebTodo(link)
  if (!target) return { kind: 'unknown', notice: 'unknown' }
  if (target.kind === 'route') return { kind: 'route', location: target.location }

  const calendars = await deps.calendars()
  if (calendars.length !== 1)
    return { kind: 'route', location: calendarsBrowseLocation, notice: 'pickList' }
  return {
    kind: 'route',
    location: {
      name: 'todo-tag',
      params: { calendarId: calendars[0].id, tag: target.tag },
      query: target.query,
    },
  }
}
